import { FC, Ref, useState } from "react";
import Dialog from "../Dialog/Dialog";
import { DialogHandle } from "../Dialog/DialogTypes";
import dialogCss from "../../common-styling/Dialog.module.css";
import { useServices } from "../../services/Services";
import { Autocomplete, TextField } from "@mui/material";
import { User } from "@unruly-osdk/sdk";
import { Osdk } from "@osdk/client";
import RadioField from "../Dialog/RadioField";
import Explanation from "../Dialog/Explanation";

interface Option {
  label: string | undefined;
  id: string;
}

const HOUR_PREFERENCE_DEFAULT = 9;
const TIMEZONE_DEFAULT = "55e6d128-1735-4767-b246-212c665fbe8c"; // London, GMT+0, which is the default in the code sending the emails.

function findSelectedTimezone(
  selectedTimezone: Option | null,
  timezoneOptions: Option[],
  currentTimezoneId: string | undefined,
): Option | null {
  if (!selectedTimezone && currentTimezoneId) {
    selectedTimezone =
      timezoneOptions.find((tz) => tz.id === currentTimezoneId) || null;
  }
  if (!selectedTimezone) {
    selectedTimezone =
      timezoneOptions.find((tz) => tz.id === TIMEZONE_DEFAULT) || null;
  }
  if (!selectedTimezone) {
    console.warn(
      "No time zone preference, and couldn't find the default time zone",
    );
  }
  return selectedTimezone;
}

const CommunicationPreferencesDialogInner: FC<{
  user: Osdk.Instance<User>;
  timezoneOptions: Option[];
  selectedTimezone: Option | null;
  setSelectedTimezone: (tz: Option | null) => void;
}> = function ({
  user,
  timezoneOptions,
  selectedTimezone,
  setSelectedTimezone,
}) {
  const [optOutSelected, setOptOutSelected] = useState(
    user.optOutOfDailyReportEmails ?? false,
  );

  selectedTimezone = findSelectedTimezone(
    selectedTimezone,
    timezoneOptions,
    user.henceGlobalTimezone,
  );

  return (
    <>
      <div className={dialogCss["field-group"]}>
        <div className={dialogCss.label}>Change daily report preferences</div>
        <RadioField
          name="daily-report"
          options={[
            { label: "Receive Personal Daily Brief email", value: true },
            {
              label: "Opt out of receiving Personal Daily Brief email",
              value: false,
            },
          ]}
          selectedValue={!optOutSelected}
          setSelectedValue={(v) => setOptOutSelected(!v)}
        />
      </div>
      {optOutSelected ? (
        <></>
      ) : (
        <>
          <div className={dialogCss["field-group"]}>
            <div className={dialogCss.label}>
              Hour to email report{" "}
              <Explanation>
                0 means midnight for the specified timezone. 23 means 11pm for
                the specified timezone.
              </Explanation>
            </div>
            <div className={dialogCss.field}>
              <input
                type="number"
                min="0"
                max="23"
                required
                name="hour"
                defaultValue={
                  user.dailyReportEmailHourPreference ?? HOUR_PREFERENCE_DEFAULT
                }
              />
            </div>
          </div>

          <div className={dialogCss["field-group"]}>
            <div className={dialogCss.label}>Timezone</div>
            <div className={dialogCss.field}>
              <Autocomplete
                size="small"
                className={dialogCss.autocomplete}
                disablePortal
                options={timezoneOptions}
                isOptionEqualToValue={(o1, o2) => {
                  return o1.id === o2.id;
                }}
                value={selectedTimezone}
                renderInput={(params) => <TextField {...params} required />}
                onChange={(_e, value) => setSelectedTimezone(value)}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

const CommunicationPreferencesDialog: FC<{
  ref: Ref<DialogHandle>;
  onClose: () => void;
}> = function ({ ref, onClose }) {
  const services = useServices();
  const { data: user } = services.User.useGetCurrent();
  const { data: timezones } = services.Timezone.useGetAll();

  const timezoneOptions =
    (timezones || []).map((tz) => ({
      label: tz.name,
      id: tz.id,
    })) || [];

  const [selectedTimezone, setSelectedTimezone] = useState<Option | null>(null);

  const saveMutation = services.User.useSaveCommunicationPreferences();
  async function savePreferences(data: FormData) {
    const user = (await services.User.getCurrentAsync()) as Osdk.Instance<User>; // we always have a user here since the page has loaded
    const optOut = data.get("daily-report") === "false";
    await saveMutation.mutateAsync({
      optOut,
      hourOffset: optOut
        ? (user.dailyReportEmailHourPreference ?? HOUR_PREFERENCE_DEFAULT)
        : +(data.get("hour") as string),
      timezoneId: optOut
        ? (user.henceGlobalTimezone ?? TIMEZONE_DEFAULT)
        : findSelectedTimezone(
            selectedTimezone,
            timezoneOptions,
            user.henceGlobalTimezone,
          )!.id,
      user,
    });
  }
  function onError(error: unknown) {
    console.error(error);
    return "Something went wrong saving your preferences. If this persists, you can contact Unruly support through accounts@unrulycorp.com";
  }

  return (
    <Dialog
      title="Communication Preferences"
      action="Save Preferences"
      onSubmit={savePreferences}
      onError={onError}
      ref={ref}
      onClose={onClose}
    >
      {user && timezones ? (
        <CommunicationPreferencesDialogInner
          user={user}
          timezoneOptions={timezoneOptions}
          selectedTimezone={selectedTimezone}
          setSelectedTimezone={setSelectedTimezone}
        />
      ) : (
        "Loading..."
      )}
    </Dialog>
  );
};

export default CommunicationPreferencesDialog;
